import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

const GIS_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

interface GoogleConfigResponse {
  payload?: { clientId?: string };
}

/** The slice of the Google Identity Services API we use. */
interface GoogleIdentityApi {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        callback: (response: { credential?: string }) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
      }): void;
      renderButton(
        parent: HTMLElement,
        options: {
          type?: 'standard' | 'icon';
          theme?: 'outline' | 'filled_blue' | 'filled_black';
          size?: 'small' | 'medium' | 'large';
          text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
          shape?: 'rectangular' | 'pill' | 'circle' | 'square';
          width?: number;
          logo_alignment?: 'left' | 'center';
          locale?: string;
        }
      ): void;
      disableAutoSelect(): void;
    };
  };
}

/**
 * Loads Google Identity Services on demand and renders its sign-in button.
 *
 * The script is fetched only when a login page actually asks for it, rather than from index.html:
 * every other page in the app would otherwise pay for a third-party request it never uses.
 */
@Injectable({ providedIn: 'root' })
export class GoogleIdentityService {
  private loader: Promise<GoogleIdentityApi> | null = null;
  private clientIdRequest: Promise<string> | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Draw Google's own button into `container` and call `onCredential` with the ID token it yields.
   *
   * We render Google's button rather than styling our own: the branding rules for Sign in with
   * Google require it, and it is what keeps the flow working when Google changes it.
   *
   * Throws when sign-in is not configured or the client id can't be fetched; the caller is expected
   * to hide the button rather than block the page.
   */
  async renderButton(
    container: HTMLElement,
    onCredential: (credential: string) => void,
    locale?: string
  ): Promise<void> {
    const [clientId, google] = await Promise.all([this.fetchClientId(), this.load()]);

    if (!clientId) {
      throw new Error('Google sign-in is not configured (the API returned no client id)');
    }

    google.accounts.id.initialize({
      client_id: clientId,
      callback: response => {
        if (response?.credential) {
          onCredential(response.credential);
        }
      },
      // No One Tap / auto sign-in: the user clicks the button when they mean to.
      auto_select: false,
      cancel_on_tap_outside: true
    });

    google.accounts.id.renderButton(container, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'center',
      width: container.offsetWidth || 300,
      locale
    });
  }

  /**
   * Forget the Google session hint, so the next sign-in asks which account to use instead of
   * silently reusing the last one. Called on logout.
   */
  disableAutoSelect(): void {
    const google = (window as unknown as { google?: GoogleIdentityApi }).google;
    google?.accounts?.id?.disableAutoSelect();
  }

  /**
   * The OAuth client id, from the API rather than the bundle.
   *
   * It lives server-side so there is exactly one copy: it used to be compiled into each frontend's
   * `environment.*.ts`, and rotating the OAuth client left the stale id baked into the bundle while
   * the backend was already on the new one — the browser then got Google's `deleted_client` error.
   * Now rotating it is a backend config change, no frontend rebuild.
   *
   * Cached for the lifetime of the page: the login page may render the button more than once.
   */
  private fetchClientId(): Promise<string> {
    if (this.clientIdRequest) {
      return this.clientIdRequest;
    }

    const baseUrl = (environment.apiUrl || '').replace(/\/+$/u, '');
    this.clientIdRequest = firstValueFrom(
      this.http.get<GoogleConfigResponse>(`${baseUrl}/webapp/auth/google-config`)
    )
      .then(response => (response?.payload?.clientId ?? '').trim())
      .catch(error => {
        // Don't cache a failure as an answer, or the button stays hidden until a reload.
        this.clientIdRequest = null;
        throw error;
      });

    return this.clientIdRequest;
  }

  private load(): Promise<GoogleIdentityApi> {
    if (this.loader) {
      return this.loader;
    }

    this.loader = new Promise<GoogleIdentityApi>((resolve, reject) => {
      const existing = (window as unknown as { google?: GoogleIdentityApi }).google;
      if (existing?.accounts?.id) {
        resolve(existing);
        return;
      }

      const script = document.createElement('script');
      script.src = GIS_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        const google = (window as unknown as { google?: GoogleIdentityApi }).google;
        if (google?.accounts?.id) {
          resolve(google);
        } else {
          // A failed load must not be cached as a resolved one, or the button stays blank forever.
          this.loader = null;
          reject(new Error('Google Identity Services loaded without accounts.id'));
        }
      };
      script.onerror = () => {
        this.loader = null;
        reject(new Error('Failed to load Google Identity Services'));
      };
      document.head.appendChild(script);
    });

    return this.loader;
  }
}
